import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { PlanService } from '../../Modules/Plans/Services/plan.service';
import { PlanPriceService } from '../../Modules/Plans/Services/plan-price.service';
import { PlanFeatureService } from '../../Modules/Plans/Services/plan-feature.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import {
  ROLE_PLAN_WRITE,
} from '../../Consts/plans.const';
import { PriceComponentType } from '../../Enums/price-component-type.enum';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { PlanComponent } from '../../Models/backoffice/plan-component.model';
import { PlanDetail } from '../../Models/backoffice/plan-detail.model';
import { PlanFeature } from '../../Models/backoffice/plan-feature.model';
import { PlanPrice } from '../../Models/backoffice/plan-price.model';
import { PlanSubscriber } from '../../Models/backoffice/plan-subscriber.model';
import {
  PlanPricePayload,
  PlanUpdatePayload,
} from '../../Modules/Plans/Interfaces/plan-filters.interface';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import {
  PlanPriceListResponse,
  PlanShowResponse,
  PlanSubscriberListResponse,
} from '../../Interfaces/api/backoffice/plan.interface';
import { PlanFeatureMatrixResponse } from '../../Interfaces/api/backoffice/feature.interface';

@Injectable()
export class PlanDetailFacade {
  private readonly _planService = inject(PlanService);
  private readonly _priceService = inject(PlanPriceService);
  private readonly _featureService = inject(PlanFeatureService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _planId = signal<string>('');
  private readonly _subscriberPage = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isMutating = signal<boolean>(false);

  private readonly _plan = httpResource<LaravelDefaultResponse<PlanShowResponse>>(() =>
    this._planId() ? this._planService.showRequest(this._planId()) : undefined,
  );

  private readonly _prices = httpResource<LaravelDefaultResponse<PlanPriceListResponse>>(() =>
    this._planId() ? this._priceService.listRequest(this._planId()) : undefined,
  );

  private readonly _features = httpResource<LaravelDefaultResponse<PlanFeatureMatrixResponse>>(() =>
    this._planId() ? this._featureService.matrixRequest(this._planId()) : undefined,
  );

  private readonly _subscribers = httpResource<LaravelDefaultResponse<PlanSubscriberListResponse>>(
    () =>
      this._planId()
        ? this._planService.subscribersRequest(
            this._planId(),
            this._subscriberPage(),
            this._pageSize(),
          )
        : undefined,
  );

  readonly planId = computed<string>(() => this._planId());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly plan = computed<PlanDetail | null>(() => this._planService.toDetail(this._resourceState.valueOf(this._plan)));

  readonly state = computed<MetricState>(() => this._resourceState.metricState(this._plan.status()));

  readonly prices = computed<PlanPrice[]>(() => this._priceService.toPrices(this._resourceState.valueOf(this._prices)));

  readonly components = computed<PlanComponent[]>(() =>
    this._priceService.toComponents(this._resourceState.valueOf(this._prices)),
  );

  readonly pricesState = computed<MetricState>(() =>
    this._resourceState.metricState(this._prices.status()),
  );

  readonly features = computed<PlanFeature[]>(() =>
    this._featureService.toMatrix(this._resourceState.valueOf(this._features)),
  );

  readonly grantedFeatureIds = computed<string[]>(() =>
    this._featureService.toGrantedIds(this._resourceState.valueOf(this._features)),
  );

  readonly featuresState = computed<MetricState>(() =>
    this._resourceState.metricState(this._features.status()),
  );

  readonly subscribers = computed<PlanSubscriber[]>(
    () => this._planService.toSubscribers(this._resourceState.valueOf(this._subscribers)).items,
  );

  readonly subscriberTotal = computed<number>(
    () => this._planService.toSubscribers(this._resourceState.valueOf(this._subscribers)).total,
  );

  readonly subscribersState = computed<MetricState>(() =>
    this._resourceState.metricState(this._subscribers.status()),
  );

  readonly subscriberPage = computed<number>(() => this._subscriberPage());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly canWrite = computed<boolean>(() => this._operatorFacade.atLeast(ROLE_PLAN_WRITE));

  readonly seatComponent = computed<PlanComponent | null>(
    () =>
      this.components().find(
        (component) => component.component_type === PriceComponentType.SEAT,
      ) ?? null,
  );

  readonly agentComponent = computed<PlanComponent | null>(
    () =>
      this.components().find(
        (component) => component.component_type === PriceComponentType.AGENT,
      ) ?? null,
  );

  readonly agentIsPermitted = computed<boolean>(() => this.agentComponent()?.is_permitted ?? false);

  load(planId: string): void {
    this._planId.set(planId);
  }

  goToSubscriberPage(page: number): void {
    this._subscriberPage.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._subscriberPage.set(FIRST_PAGE);
  }

  reload(): void {
    this._plan.reload();
    this._prices.reload();
    this._features.reload();
    this._subscribers.reload();
  }

  save(payload: PlanUpdatePayload): void {
    const planId = this._planId();

    if (!planId) {
      return;
    }

    this._isMutating.set(true);

    this._planService
      .update(planId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterPlanWrite('backoffice.plans.detail.updated'),
        error: (error) => this._failMutation(error),
      });
  }

  createPrice(payload: PlanPricePayload): void {
    const planId = this._planId();

    if (!planId) {
      return;
    }

    this._isMutating.set(true);

    this._priceService
      .create(planId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterPriceWrite('backoffice.plans.prices.created'),
        error: (error) => this._failMutation(error),
      });
  }

  updatePrice(priceId: string, payload: Partial<PlanPricePayload>): void {
    const planId = this._planId();

    if (!planId) {
      return;
    }

    this._isMutating.set(true);

    this._priceService
      .update(planId, priceId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterPriceWrite('backoffice.plans.prices.updated'),
        error: (error) => this._failMutation(error),
      });
  }

  deactivatePrice(priceId: string): void {
    const planId = this._planId();

    if (!planId) {
      return;
    }

    this._isMutating.set(true);

    this._priceService
      .deactivate(planId, priceId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterPriceWrite('backoffice.plans.prices.deactivated'),
        error: (error) => this._failMutation(error),
      });
  }

  syncFeatures(featureIds: string[]): void {
    const planId = this._planId();

    if (!planId) {
      return;
    }

    this._isMutating.set(true);

    this._featureService
      .sync(planId, featureIds)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterFeatureWrite(),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterPlanWrite(message: string): void {
    this._isMutating.set(false);
    this._plan.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _afterPriceWrite(message: string): void {
    this._isMutating.set(false);
    this._prices.reload();
    this._plan.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _afterFeatureWrite(): void {
    this._isMutating.set(false);
    this._features.reload();
    this._prices.reload();
    this._plan.reload();
    this._msgService.showSnackBar('backoffice.plans.features.saved', { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
